import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Save, ArrowLeft } from "lucide-react";
import { useCRMStore } from "../store/crmStore";
import type { Contact } from "../types/crm";
import SEO from "./SEO";
import LoadingSpinner from "../../packages/ui/src/components/LoadingSpinner";

type ContactFormData = Omit<Contact, "id" | "created_at" | "updated_at">;

const INITIAL_FORM_DATA: ContactFormData = {
  first_name: "",
  last_name: "",
  company: "",
  company_url: "",
  email: "",
  phone: "",
  status: "prospect",
  source: "",
  last_contact_date: undefined,
  custom_fields: {},
  deleted: false,
};

export default function AdminCRMContactForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    selectedContact,
    loading,
    error,
    fetchContactDetails,
    createContact,
    updateContact,
  } = useCRMStore();

  const [formData, setFormData] = useState<ContactFormData>(INITIAL_FORM_DATA);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (id) {
      fetchContactDetails(id);
    }
  }, [id, fetchContactDetails]);

  useEffect(() => {
    if (selectedContact) {
      setFormData({
        first_name: selectedContact.first_name,
        last_name: selectedContact.last_name,
        company: selectedContact.company || "",
        company_url: selectedContact.company_url || "",
        email: selectedContact.email,
        phone: selectedContact.phone || "",
        status: selectedContact.status,
        source: selectedContact.source || "",
        last_contact_date: selectedContact.last_contact_date,
        custom_fields: selectedContact.custom_fields || {},
        deleted: selectedContact.deleted || false,
      });
    }
  }, [selectedContact]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      errors.first_name = "First name is required";
    }

    if (!formData.last_name.trim()) {
      errors.last_name = "Last name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (formData.phone && !/^\+?[0-9\s-()]+$/.test(formData.phone)) {
      errors.phone = "Please enter a valid phone number";
    }

    // Simpler URL validation - just check for a valid domain format
    if (formData.company_url) {
      const urlPattern = /^[A-Za-z0-9][A-Za-z0-9.-]*\.[A-Za-z]{2,}.*$/;
      if (!urlPattern.test(formData.company_url)) {
        errors.company_url = "Please enter a valid domain (e.g., example.com)";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);

      // Clean up company URL format
      let normalizedData = { ...formData };
      if (normalizedData.company_url) {
        // Remove any existing protocol
        normalizedData.company_url = normalizedData.company_url.replace(
          /^https?:\/\//i,
          "",
        );
        // Remove www. if present
        normalizedData.company_url = normalizedData.company_url.replace(
          /^www\./i,
          "",
        );
        // Remove trailing slashes
        normalizedData.company_url = normalizedData.company_url.replace(
          /\/+$/,
          "",
        );
      }

      if (id) {
        await updateContact(id, normalizedData);
      } else {
        await createContact(normalizedData);
      }

      navigate("/admin/crm/contacts");
    } catch (error) {
      console.error("Error saving contact:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <SEO
        title={`One80Labs - ${id ? "Edit" : "New"} CRM Contact`}
        description="Create or edit a contact in the One80Labs CRM system."
        noindex={true}
      />
      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={() => navigate("/admin/crm/contacts")}
          className="inline-flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Contacts
        </button>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c] disabled:opacity-50"
        >
          <Save className="h-5 w-5 mr-2" />
          {saving ? "Saving..." : "Save Contact"}
        </button>
      </div>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData({ ...formData, first_name: e.target.value })
                  }
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    validationErrors.first_name
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  }`}
                  required
                />
                {validationErrors.first_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.first_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData({ ...formData, last_name: e.target.value })
                  }
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    validationErrors.last_name
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  }`}
                  required
                />
                {validationErrors.last_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.last_name}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  validationErrors.email
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                }`}
                required
              />
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.email}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className={`mt-1 block w-full rounded-md shadow-sm ${
                  validationErrors.phone
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                }`}
                placeholder="e.g., +1 (555) 123-4567"
              />
              {validationErrors.phone && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.phone}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Company
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) =>
                    setFormData({ ...formData, company: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Company Website
                </label>
                <input
                  type="text"
                  value={formData.company_url}
                  onChange={(e) =>
                    setFormData({ ...formData, company_url: e.target.value })
                  }
                  className={`mt-1 block w-full rounded-md shadow-sm ${
                    validationErrors.company_url
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  }`}
                  placeholder="example.com"
                />
                {validationErrors.company_url && (
                  <p className="mt-1 text-sm text-red-600">
                    {validationErrors.company_url}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Enter domain only (e.g., example.com)
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      status: e.target.value as "prospect" | "customer",
                    })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                >
                  <option value="prospect">Prospect</option>
                  <option value="customer">Customer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Source
                </label>
                <input
                  type="text"
                  value={formData.source}
                  onChange={(e) =>
                    setFormData({ ...formData, source: e.target.value })
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  placeholder="How did they find us?"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Last Contact Date
              </label>
              <input
                type="datetime-local"
                value={
                  formData.last_contact_date
                    ? new Date(formData.last_contact_date)
                        .toISOString()
                        .slice(0, 16)
                    : ""
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    last_contact_date: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
